import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { Flip } from 'gsap/Flip';
import { getLenis, prefersReducedMotion } from './lenis';

/**
 * GSAP plugin registration + Lenis sync.
 *
 * CRITICAL — per PRD §10.1 + PLAN TASK 02 acceptance criteria:
 *   1. Lenis becomes the scroll source: `lenis.on('scroll', ScrollTrigger.update)`
 *   2. GSAP ticker drives Lenis raf: `gsap.ticker.add(t => lenis.raf(t * 1000))`
 *   3. `gsap.ticker.lagSmoothing(0)` — no time skipping on lag
 *
 * After this runs, Lenis-driven scroll triggers ScrollTrigger.update on every
 * frame, and ScrollTrigger reports the correct scroll position synchronously.
 * This is the single most common source of "ScrollTrigger does not respond
 * to Lenis" bugs — fixing it once here prevents whack-a-mole later.
 */

// Register plugins eagerly at module scope — any component that imports from
// `@/lib/gsap` gets plugin availability guaranteed before their effects run.
gsap.registerPlugin(ScrollTrigger, SplitText, Flip);

let _lenisSynced = false;

/**
 * Sync Lenis ↔ GSAP ScrollTrigger.  Called once by RootLayout after Lenis is
 * created. Safe to call multiple times (idempotent).
 */
export function registerGsap(): void {
  if (_lenisSynced) return;

  if (typeof window === 'undefined') {
    _lenisSynced = true;
    return;
  }

  if (prefersReducedMotion()) {
    _lenisSynced = true;
    return;
  }

  const lenis = getLenis();
  if (lenis) {
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);
  }

  // Refresh ScrollTrigger after fonts load — avoids early measurement on
  // un-fontswapped headings (which causes y-offset drift).
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
  }

  _lenisSynced = true;
}

export { gsap, ScrollTrigger, SplitText, Flip };
